import React, {
  forwardRef,
  FunctionComponent,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import * as entities from '../../graphql/entities';
import { PurchaseOrder, PurchaseOrderAuditByField, PurchaseOrderLine, Supplier } from '../../graphql/entities';
import productDropdown from '../../utils/GridDropdownSetup';
import DropdownEditor from '../../components/DropdownEditor';
import getIn from '../../components/connectedForm/utils/getIn';
import setIn from '../../components/connectedForm/utils/setIn';
import { addressUtil } from '../../utils/AddressUtils';
import { default as Form } from '../../components/connectedForm';
import Formatters from '../../components/grid-addons/formatters';
import EditableDataGrid from '../../components/EditableDataGrid';
import InputBlock from '../../components/connectedForm/InputBlock';
import InputGroup from '../../components/connectedForm/InputGroup';
import InputLabel from '../../components/connectedForm/InputLabel';
import { change } from '../../components/connectedForm/utils/actions';
import DiaryEventWorkspace from '../diary/DiaryEventWorkspace';
import * as input from '../../components/connectedForm/inputComponents';
import CheckPopUpBlockerFunction from '../../utils/CheckPopUpBlockerFunction';
import OrderTotals, { OrderTotalsOriginModule } from '../../components/CommonOrderComponents/OrderTotals';
import ValueBreakdown from '../../components/CommonOrderComponents/ValueBreakdown';
import LineDetailsPanel from '../../components/CommonOrderComponents/LineDetailsPanel';
import GridContainer from '../../components/connectedForm/GridContainer';
import AuditByFieldWorkspace from '../company/AuditByFieldWorkspace';
import ColumnLabel from '../../components/connectedForm/ColumnLabel/ColumnLabel';
import Label from '../../components/connectedForm/Label/Label';
import PurchaseOrderHeaderComponent, {
  IPurchaseOrderHeaderComponentProps,
} from '../../components/PurchaseOrderHeader/PurchaseOrderHeaderComponent';
import createForm from '../../components/connectedForm/ConnectedForm';
import { PurchaseOrderDocumentRenderer, submitValues } from '../../utils/PurchaseOrderFormUtil';
import { useDispatch } from 'react-redux';
import {
  useCompany,
  useDocumentConfig,
  usePurchaseOrderConfig,
  useSalesOrderConfig,
  useSupplierConfig,
} from '../../utils/FetchConfig';
import DocumentSummaryWorkspace from '../company/DocumentSummaryWorkspace';
import fieldMetaData, { IEnumMeta } from '../../graphql/schema/fieldMetaData';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { PriceOption } from '../../components/CommonOrderComponents/PriceOption';
import {
  entryGridWithRowUpdated,
  getSellingPricePrecision,
  grossPriceCreatePrompt,
  grossPriceOptionCreator,
  minimumQuantityToUse,
  normalizeOrderLines,
  roundByPrecision,
  useGetCostPricePrecision,
} from '../../components/CommonOrderComponents/CommonOrderFunctions';
import DropdownEditorClean from '../../components/DropdownEditor/DropdownEditorClean';
import { appActions } from '../../state/modules/app';
import CheckboxEditor from '../../components/CheckboxInputEditor/CheckboxInputEditor';
import CurrencyContext from '../../components/Context/CurrencyContext';
import { Description, DescriptionPill, Option } from '../../components/DropdownEditor/CustomOptions';
import Price from '../../components/connectedForm/Price';
import ConnectedFormContext, { ConnectedFormContextType } from '../../components/Context/ConnectedFormContext';
import ContextMenuItem, { IContextMenuItemProps } from '../../components/ContextMenuItem';
import { getIdFromProps, getIdFromPropsForFilter } from '../../utils/formUtils';
import { getSalesOrderStatusIcon } from '../../utils/SalesUtils';
import PurchaseOrderLineItemInformationQuery from '../../graphql/queries/purchases/PurchaseOrderLineItemInformation.graphql';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import FluentTextField from '../../components/FluentWrapper/FluentTextField';
import { ConnectedFormPristinePrompt } from '../../components/connectedForm/ConnectedFormPristinePrompt';
import {
  AlertType,
  BooleanOperator,
  CostViewPermission,
  Direction,
  FilterOperation,
  IAddressInput,
  ICustomer,
  ICustomerContact,
  IFilterInput,
  IPricePotential,
  IProduct,
  IProductSupplierDetails,
  IPurchaseCost,
  IPurchaseCostTemplate,
  IPurchaseInvoiceOrderLineMatch,
  IPurchaseNotesQueryResult,
  IPurchaseNotesQueryVariables,
  IPurchaseOrder,
  IPurchaseOrderLine,
  IPurchaseOrderLinesWorkspaceQueryResult,
  IPurchaseOrderLinesWorkspaceQueryVariables,
  ISupplier,
  ISupplierBasicQueryResult,
  ISupplierBasicQueryVariables,
  ISupplierNotesQueryResult,
  ISupplierNotesQueryVariables,
  Maybe,
  PurchaseOrderLineStatus,
  PurchaseOrderMatchingType,
} from '../../graphql/schema/p4types';
import {
  ICommit,
  IEditableDataGridColumnDef,
  IEditableDataGridDropdownOptions,
} from '../../@types/editableDataGridTypes';
import { IDropdownProps } from '../../@types/dropdown';
import { AddressInfo } from '../../components/CommonOrderComponents/AddressInfo';
import {
  IEditableDataGridProps,
  IOnCellSelectedFunction,
  IRowUpdatedFunction,
} from '../../components/EditableDataGrid/EditableDataGrid';
import {
  BlurTextFieldType,
  HrefLink,
  IBlurTextFieldProps,
} from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { ITextFieldProps } from '@fluentui/react/lib/TextField';
import { IGridUploaderProps } from '../../components/connectedForm/GridUploader';
import { EntityType, IDropdownOptionRenderer } from '../../@types/entityTypes';
import CommentRowContext from '../../components/Context/CommentRowContext';
import { getEntryCount } from '../../utils/CountUtil';
import { IStatusFlag, IStatusFlagStatus } from '../../components/connectedForm/HeaderBlock/HeaderBlock';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import GoodsReceiptLineWorkspace from '../product/GoodsReceiptLineWorkspace';
import { useHistory } from 'react-router';
import AppContext from '../../components/Context/AppContext';
import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../../components/connectedForm/MemoEditor/MemoEditor';
import SupplierNotesWorkspaceQuery from '../../graphql/queries/supplier/SupplierNotesWorkspaceQuery.graphql';
import PurchaseNotesWorkspaceQuery from '../../graphql/queries/purchases/PurchaseNotesWorkspaceQuery.graphql';
import PurchaseNoteType from '../../graphql/entities/purchases/PurchaseNoteType';
import SupplierNoteType from '../../graphql/entities/supplier/SupplierNoteType';
import useEntityCacheUpdate from '../../graphql/hooks/useEntityCacheUpdate';
import getProductDP from '../../utils/getProductDP';
import { IConnectedFormProps } from '../../@types/connectedFormTypes';
import { serviceActions } from '../../state/modules/service';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import multiListQuery from '../../graphql/queries/product/ProductMultiListQuery.graphql';
import { getMultiQueryKeys } from '../../graphql/entities/product/Product';
import { PurchaseOrderStatusCellFormatter } from '../../components/CellFormatters/PurchaseOrderStatusCellFormatter';
import { AbstractLineGrid } from '../../components/EditableDataGrid/AbstractLineGrid';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import SidebarContext, { SidebarType } from '../../components/Context/SidebarContext';
import { ConnectedAddress } from '../../components/connectedForm/ConnectedAddress';
import { StatusStyle } from '../../components/connectedForm/HeaderStatus';
import useInstrastatHints from '../../utils/useInstrastatHints';
import { useP4ToastController } from '../system/NotificationsArea';
import { add } from 'date-fns';
import { useCookies } from 'react-cookie';
import { ICommentRowRendererProps } from '../../components/EditableDataGrid/CommentRowRenderer';
import { StandaloneGlobalContactLookupWrapper } from '../../components/modals/CustomerGlobalContactLookup';

const TypedCheckboxEditor: FunctionComponent<any> = CheckboxEditor as any;

const LazyPurchaseOrderLineWorkspace = React.lazy(() => import('../../views/purchases/PurchaseOrderLineWorkspace'));
const LazyCommentRowRenderer = React.lazy(() => import('../../components/EditableDataGrid/CommentRowRenderer'));
const LazyPurchaseInvoiceForm: any = React.lazy(() => import('../supplier/PurchaseInvoiceForm'));

const {
  default: defaultQuery,
  temporarySave,
  formTitle,
  instance,
  workspaceUrl,
  update,
  fragment,
  type,
} = PurchaseOrder;

const ContactOptionRenderer: IDropdownOptionRenderer = (props: { option: ICustomerContact }) => {
  const contact = props.option;
  return <>{`${contact?.name?.fullName} - ${contact?.customer?.name}`}</>;
};

const NextPriceBreakFormatter = ({ rowIdx, value }: { rowIdx: number; value: Array<Maybe<IPricePotential>> }) => {
  const connectedForm = useContext(ConnectedFormContext);

  if (!value) return null;

  const currentQuantity = connectedForm.values?.orderLines?.[rowIdx]?.quantity || 0;

  //Get the smallest price break that is greater than the current quantity on the line.
  const nextBreak = value.reduce((accumulator, currentValue) => {
    if (currentValue && currentValue.priceBreak > currentQuantity) {
      if (!accumulator) return currentValue;
      if (accumulator.priceBreak < currentValue.priceBreak) return accumulator;
      return currentValue;
    }
    return accumulator;
  }, null);

  if (!nextBreak) return null;

  return (
    <Option>
      <Price type={'currency'} decimals={2}>
        {nextBreak.nettPrice}
      </Price>
      <Description>
        <DescriptionPill>{nextBreak.priceBreak}+</DescriptionPill>
      </Description>
    </Option>
  );
};

const HeadOfficeInfo = ({ purchaseOrder }: { purchaseOrder: IPurchaseOrder }) => {
  let headOffice: Maybe<ISupplier> = null;
  if (!purchaseOrder?.supplier) return null;

  if (purchaseOrder.supplier.headOffice) {
    headOffice = purchaseOrder.supplier.headOffice;
  } else {
    headOffice = purchaseOrder.supplier;
  }
  if (!headOffice) return null;

  return (
    <>
      <Form.Legend label="Invoice Account" />
      <InputBlock>
        <InputLabel />
        <InputGroup>
          <a href={`/Suppliers/Supplier/Details/${headOffice.id}`} target="_null">
            {headOffice.reference} - {headOffice.name}
          </a>
        </InputGroup>
        <InputLabel />
        <InputGroup>{addressUtil(headOffice.address as Maybe<IAddressInput>)};</InputGroup>
      </InputBlock>
    </>
  );
};

const PurchaseOrderRowRenderer = (props: ICommentRowRendererProps) => {
  const [cookies] = useCookies([HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE]);

  const shouldHideRow =
    cookies.HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE === 'true' &&
    props.row?.invoicedQuantity > 0 &&
    props.row.invoicedQuantity === props.row?.quantity;

  if (shouldHideRow) {
    return null;
  }

  return <LazyCommentRowRenderer {...props} />;
};

const ASSIGNED_COLUMNS = [
  {
    name: 'Order Number',
    key: 'salesOrderLine.salesOrder.orderNumber',
  },
  {
    name: 'Customer Reference',
    key: 'salesOrderLine.salesOrder.customer.reference',
  },
  {
    name: 'Customer Name',
    key: 'salesOrderLine.salesOrder.customer.name',
  },
  {
    name: 'Order Quantity',
    key: 'salesOrderLine.quantity',
  },
  {
    name: 'Assigned',
    key: 'assigned',
  },
  {
    name: 'Allocated',
    key: 'salesOrderLine.allocated',
  },
  {
    name: 'Outstanding',
    key: 'salesOrderLine.outstanding',
  },
];

const ASSEMBLY_ASSIGNED_COLUMNS = [
  {
    name: 'Order Number',
    key: 'assembly.orderNumber',
  },
  {
    name: 'Ref',
    key: 'assembly.reference',
  },
];

const PURCHASE_ORDER_STATUS_FLAGS: Array<IStatusFlag> = [
  {
    key: 'vatInclusive',
    status: [
      {
        value: true,
        style: StatusStyle.Info,
        title: 'VAT INCLUSIVE',
      },
    ],
  },
  {
    key: 'foreignCurrency',
    status: [
      {
        value: true,
        style: StatusStyle.Info,
        icon: 'account_balance',
        title: 'Currency',
        titleKey: 'currency.isoCode.codeAlpha3',
      },
    ],
  },
  {
    key: 'directDelivery',
    status: [
      {
        value: true,
        style: StatusStyle.Info,
        icon: 'local_shipping',
        title: 'Direct Delivery',
        tooltip: 'This order will be directly delivered to a Customer.',
      },
    ],
  },
  {
    key: 'archived',
    status: [
      {
        value: true,
        style: StatusStyle.Danger,
        title: 'Archived',
      },
    ],
  },
  {
    key: 'status',
    status: fieldMetaData.PurchaseOrderLineStatus.enumValues.map((entry: IEnumMeta<PurchaseOrderLineStatus>) => {
      const icon = getSalesOrderStatusIcon(entry.value);
      return {
        value: entry.value,
        style: icon.color,
        title: entry.label,
        icon: icon.type,
      } as IStatusFlagStatus;
    }),
  },
];

const PURCHASE_COST_COLUMN_META: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Description',
    key: 'description',
  },
  {
    name: 'Supplier',
    key: 'defaultSupplier',
    options: entities.Supplier,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
  },
  {
    name: 'Currency',
    key: 'currency',
    options: entities.Currency,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
  },
  {
    name: 'Cost',
    key: 'cost',
    options: {
      format: {
        decimals: getSellingPricePrecision,
      },
    },
    formatter: 'numeric',
  },
  {
    name: 'Distribution',
    key: 'chargeBasis',
    editor: <DropdownEditor options={fieldMetaData.ChargeBasis.enumValues} />,
    formatter: Formatters.EnumFormatter,
    options: {
      ...fieldMetaData.ChargeBasis,
      type: 'ChargeBasis',
      dropdown: {
        isEnum: true,
        clearable: true,
      },
    },
  },
];

const usePurchaseOrderFormFilters: (id?: string) => {
  eventPredefinedFilters: Array<IFilterInput>;
  purchaseOrderAuditPredefinedFilters: Array<IFilterInput> | undefined;
} = (id?: string) => {
  const purchaseOrderAuditPredefinedFilters: Maybe<Array<IFilterInput>> =
    id && id !== 'New'
      ? [
          {
            filterOperation: FilterOperation.Equals,
            filterParameters: {
              field: 'id',
              value: id,
            },
          },
        ]
      : undefined;
  const eventPredefinedFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'purchaseOrder.id',
        value: id && id !== 'New' ? id : '0',
      },
    },
  ];

  return { purchaseOrderAuditPredefinedFilters, eventPredefinedFilters };
};

const dataStoreId = 'haggleWindow';

const HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE = 'HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE';

const isLineFullyInvoiced = (purchaseOrderLine: Maybe<IPurchaseOrderLine>): boolean => {
  return (
    purchaseOrderLine?.invoicedQuantity >= purchaseOrderLine?.standardUnitQuantity &&
    purchaseOrderLine?.invoicedQuantity === purchaseOrderLine?.received
  );
};

const PurchaseOrderForm = (
  props: IConnectedFormProps<IPurchaseOrder> & {
    values: IPurchaseOrder;
    match?: any;
    saveOptions?: IPurchaseOrderHeaderComponentProps['saveOptions'];
    saveViaWrappingComponent?: boolean;
    change: ConnectedFormContextType['change'];
  }
) => {
  const { values: purchaseOrder, formData, saveOptions, saveViaWrappingComponent, match } = props;
  const selectedRow = useRef<any>();
  const { modeOfTransportHint, natureOfTransactionHint, customsProcedureCodeHint } = useInstrastatHints();
  const dispatch = useDispatch();
  const sidebarContext = useContext(SidebarContext);

  const history = useHistory<{ defaultValues?: [{ field: string; value: string }] }>();
  const company = useCompany();
  const salesOrderConfig = useSalesOrderConfig();
  const documentConfig = useDocumentConfig();
  const [productModalState, setProductModalState] = useState({ modalOpen: false });
  const [cookies, setCookie] = useCookies([HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE]);
  const { dispatchSimpleToast } = useP4ToastController();
  const orderConfig = usePurchaseOrderConfig();

  const purchaseOrderEntryGridWithRowUpdated = useCallback<IRowUpdatedFunction<IPurchaseOrderLine>>(
    (
      commit: ICommit<IPurchaseOrderLine>,
      dataid?: string,
      { connectedForm: { values } = {} }: { connectedForm?: { values?: IPurchaseOrder } } = {}
    ) => {
      values?.orderLines?.forEach((orderLine) => {
        if (orderLine?.product?.id === commit?.updated?.product?.id && commit?.original?.uuid !== orderLine?.uuid) {
          const alertText = `${orderLine?.product?.description} has already been added.`;

          dispatch(serviceActions.genericAlert('Product already added', alertText, AlertType.Info));
        }
      });

      //Call common withRowUpdated first.
      entryGridWithRowUpdated<IPurchaseOrderLine>(commit as any, { connectedForm: { values } });

      //Then set the order quantity from the product supplier details record.
      if (commit.cellKey === 'product') {
        const product = commit.updated?.product as IProduct;
        if (product) {
          commit.cellKeys = ['product', 'quantity'];
          const supplierDetails: Maybe<IProductSupplierDetails> = product.alternateSupplierDetails?.find(
            (asd) => asd?.supplier?.id === values?.supplier?.id
          );

          (commit.updated as IPurchaseOrderLine).quantity = minimumQuantityToUse(
            supplierDetails?.orderQuantityMinimum,
            supplierDetails?.orderQuantityIncrement
          );
        }
      }

      if (orderConfig?.automaticallySetLineDeliveryDates && !commit.original && commit.cellKey !== 'targetDate') {
        commit.cellKeys.push('targetDate');
        (commit.updated as IPurchaseOrderLine).targetDate = values.targetDate;
      }
    },
    [dispatch, orderConfig?.automaticallySetLineDeliveryDates]
  );

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize, false);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (orderConfig?.generateIntrastat) {
      dispatchSimpleToast('Intrastat / Terms of Delivery', 'Check details in Customs tab', AlertType.Info);
    }
  }, [dispatchSimpleToast, orderConfig?.generateIntrastat]);

  const [itemTableHeight, setItemTableHeight] = useState<Maybe<number>>(null);

  const [lineDetailsSelectedRowUuid, setLineDetailsSelectedRowUuid] = useState<Maybe<string>>();
  const lineDetailsSelectedRowIndex = useMemo(() => {
    return purchaseOrder.orderLines?.findIndex((orderLine) => orderLine?.uuid === lineDetailsSelectedRowUuid);
  }, [lineDetailsSelectedRowUuid, purchaseOrder.orderLines]);

  const [addCommentToRowIdx, setAddCommentToRowIdx] = useState<Maybe<number>>(null);
  const [rowIdx, setRowIdx] = useState<Maybe<number>>();

  const id = getIdFromProps(props);
  const { purchaseOrderAuditPredefinedFilters, eventPredefinedFilters } = usePurchaseOrderFormFilters(id);

  const appContext = useContext(AppContext);

  useEffect(() => {
    if (!formData.loading) {
      const defaultValues = history?.location?.state?.defaultValues;

      if (defaultValues) {
        defaultValues.forEach((defaultValue) => {
          dispatch(change(PurchaseOrder.type, defaultValue.field, defaultValue.value));
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.loading]);

  const supplierMemoEditorProps = useMemoEditorProps<ISupplierNotesQueryResult, ISupplierNotesQueryVariables>(
    SupplierNotesWorkspaceQuery,
    IMemoEditorType.Supplier,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'supplier.id',
            value: purchaseOrder.supplier?.id,
          },
        },
      ],
      skip: !purchaseOrder.supplier?.id,
    }
  );
  const purchaseMemoEditorProps = useMemoEditorProps<IPurchaseNotesQueryResult, IPurchaseNotesQueryVariables>(
    PurchaseNotesWorkspaceQuery,
    IMemoEditorType.PurchaseOrder,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'purchaseOrder.id',
            value: purchaseOrder.id,
          },
        },
      ],
    }
  );

  const currencyCode = useMemo<Maybe<string>>(() => {
    const foreignCurrency = purchaseOrder?.foreignCurrency ?? false;
    if (foreignCurrency) {
      return purchaseOrder.currency?.isoCode?.codeAlpha3;
    }
    return null;
  }, [purchaseOrder.currency?.isoCode?.codeAlpha3, purchaseOrder?.foreignCurrency]);

  const onCellSelected: IOnCellSelectedFunction = (dataid, selected) => {
    setRowIdx(selected.rowIdx);
  };

  useQuery<ISupplierBasicQueryResult, ISupplierBasicQueryVariables>(Supplier.basic as DocumentNode, {
    skip: !match?.params?.supplierid,
    variables: { id: match?.params?.supplierid },
    onCompleted: (response) => {
      if (response?.supplier) {
        dispatch(change(PurchaseOrder.type, 'supplier', response.supplier));
      }
    },
  });

  const getCostPricePrecision = useGetCostPricePrecision();

  const allCostViewPermission: boolean =
    appContext?.fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All;

  const columnMeta = useMemo(() => {
    const configuredProductDropdown = productDropdown(salesOrderConfig);

    const noTemplateFilter: IFilterInput = {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'template',
        value: 'false',
      },
    };

    const getSalesProductLookupData = () => {
      return {
        supplier: purchaseOrder.supplier,
        includeSupplierToggle: true,
        excludeLuckins: true,
        disableCherryPick: true,
      };
    };

    const newColumnMeta: Array<IEditableDataGridColumnDef> = [
      {
        name: 'Product',
        key: 'product',
        editable: (rowData) => {
          return !rowData.received || rowData.received === 0;
        },
        editor: DropdownEditorClean,
        options: {
          ...configuredProductDropdown,
          dropdown: {
            ...configuredProductDropdown.dropdown,
            clearable: false,
            selectModal: 'SalesProductLookup',
            selectModalData: getSalesProductLookupData,
            selectModalUpdateOpen: (newState) => {
              setProductModalState({ modalOpen: newState });
            },
            predefinedFilters: [
              {
                filterOperation: FilterOperation.Equals,
                filterParameters: {
                  field: 'template',
                  value: 'false',
                },
              },
            ],
            enableMulti: true,
            temporaryOnClick: true,
            multiQuery: {
              query: multiListQuery,
              keys: getMultiQueryKeys({ productFilters: [noTemplateFilter] }),
            },
            customVariables: () => {
              return {
                includeAlternateSupplierDetails: true,
              };
            },
          },
        } as IEditableDataGridDropdownOptions,
        formatter: Formatters.ReferenceWithSupplierPartNumberPillFormatter,
        widthFactor: 2.3,
        resizable: false,
      },
      {
        name: 'Description',
        key: 'description',
        editable: true,
        resizable: true,
        widthFactor: 2.3,
      },
      {
        name: 'Quantity',
        key: 'quantity',
        formatter: 'numeric',
        editable: (rowData) => {
          return !rowData.complete;
        },
        resizable: true,
        options: {
          title: (rowData) => {
            if (rowData.complete) {
              return 'Quantity cannot be edited, line is marked as complete.';
            }
            return null;
          },
          format: { decimals: getProductDP },
          info: (rowData: IPurchaseOrderLine) => {
            if (!rowData?.potentialPrices) return null;
            const currentQuantity = rowData?.quantity;
            const nextBreak = rowData.potentialPrices.reduce((accumulator, currentValue) => {
              if (currentValue && currentValue.priceBreak > currentQuantity) {
                if (!accumulator) return currentValue;
                if (accumulator.priceBreak < currentValue.priceBreak) return accumulator;
                return currentValue;
              }
              return accumulator;
            }, null);
            if (!nextBreak) return null;
            return `Next Break: ${nextBreak.nettPrice} for ${nextBreak.priceBreak}+`;
          },
        },
      },
      {
        name: 'Unit',
        key: 'activeSellingUnit',
        editor: <DropdownEditor />,
        options: {
          dropdown: {
            labelKey: 'description',
            valueKey: 'description',
            options: 'potentialUnits',
          },
          title: (rowData) => {
            if (rowData.complete) {
              return 'Quantity cannot be edited, line is marked as complete.';
            }
            return null;
          },
        },
        editable: (rowData) => {
          return !rowData.complete && (!rowData.received || rowData.received === 0);
        },
        resizable: true,
        formatter: 'dropdown',
      },
    ];
    if (allCostViewPermission) {
      newColumnMeta.push(
        {
          name: `Cost Price ${currencyCode ? `(${currencyCode})` : ''}`,
          key: 'grossSellingPrice',
          editor: <DropdownEditorClean />,
          options: {
            dropdown: {
              labelKey: 'nettPrice',
              valueKey: 'nettPrice',
              selectionKey: 'grossPrice',
              rowSelectionKeys: ['termA', 'termB', 'termC'],
              options: 'potentialPrices',
              optionRenderer: PriceOption,
              creatable: true,
              promptTextCreator: grossPriceCreatePrompt,
              newOptionCreator: grossPriceOptionCreator,
              useLatestReactSelect: true,
              getGroupLabel: (group) =>
                (group.options.length > 0 && group.options[0].priceOriginDescription) || 'Unknown price group',
              formatNewValue: (value) => roundByPrecision(value, getCostPricePrecision()),
            },
            format: {
              decimals: getCostPricePrecision,
            },
          },
          formatter: 'numeric',
          editable: (purchaseOrderLine: IPurchaseOrderLine) => {
            return !isLineFullyInvoiced(purchaseOrderLine);
          },
          resizable: true,
        },
        {
          name: 'Next Price Break',
          key: 'potentialPrices',
          editable: false,
          resizable: true,
          formatter: NextPriceBreakFormatter,
        },
        {
          name: 'Discount %',
          key: 'discountPercentage',
          formatter: 'numeric',
          resizable: false,
          editable: (purchaseOrderLine: IPurchaseOrderLine) => {
            return !isLineFullyInvoiced(purchaseOrderLine);
          },
          options: {
            format: {
              decimals: 2,
            },
          },
          temporaryOnChange: true,
        },
        {
          name: `Nett Price ${currencyCode ? `(${currencyCode})` : ''}`,
          key: 'nettSellingPrice',
          options: {
            format: {
              decimals: getCostPricePrecision,
            },
          },
          formatter: 'numeric',
          editable: false,
          resizable: true,
        },
        {
          name: `Line Value ${currencyCode ? `(${currencyCode})` : ''}`,
          key: 'nettLineValue',
          editable: false,
          resizable: true,
          formatter: 'numeric',
          options: {
            format: {
              decimals: getCostPricePrecision,
            },
          },
        }
      );
    }

    return newColumnMeta;
  }, [allCostViewPermission, currencyCode, getCostPricePrecision, purchaseOrder.supplier, salesOrderConfig]);

  const archivedColumnMeta = useMemo(() => {
    const newArchivedColumnMeta = [...columnMeta];

    const reasonColumn = {
      name: 'Reason',
      key: 'archivedType',
      editable: false,
      resizable: true,
    };

    // PRODUCTION : Disabled context menu
    // eslint-disable-next-line no-process-env
    if (process.env.NODE_ENV !== 'production') {
      newArchivedColumnMeta.splice(newArchivedColumnMeta.length - 1, 0, reasonColumn);
    } else {
      newArchivedColumnMeta.push(reasonColumn);
    }
    return newArchivedColumnMeta;
  }, [columnMeta]);

  const onResize = () => {
    const newItemTableHeight: Maybe<number> = Math.max(window.innerHeight - 620, 175);
    setItemTableHeight(newItemTableHeight);
  };

  const onDeleteClick: IContextMenuItemProps['onClick'] = (value, close, rowIdx) => {
    if (close) close();
    dispatch(change(PurchaseOrder.type, `orderLines.${rowIdx}.archived`, true));
  };

  const onLineAuditClick: IContextMenuItemProps['onClick'] = (value, close, rowIdx) => {
    const row = purchaseOrder?.orderLines?.[rowIdx as number];
    if (row) {
      CheckPopUpBlockerFunction(
        { dispatch },
        `${window.location.origin}/Purchasing/PurchaseOrderLineAudit/Form/${purchaseOrder.id}/${row.id}`,
        '_blank'
      );
    }
  };

  const onLineProductClick: IContextMenuItemProps['onClick'] = (value, close, rowIdx) => {
    const orderLine: Maybe<IPurchaseOrderLine> = purchaseOrder.orderLines?.[rowIdx as number];
    if (orderLine && orderLine.product) {
      if (!sidebarContext?.openSidebar) {
        dispatch(appActions.toggleSidebar(false, orderLine.product.id, 'ProductForm'));
      } else {
        sidebarContext.openSidebar(SidebarType.ProductForm, orderLine.product.id);
      }
    }
  };

  const onAddLineCommentClick: IContextMenuItemProps['onClick'] = (value, close, rowIdx) => {
    if (close) close();
    setAddCommentToRowIdx(rowIdx);
  };

  const onLineChange = (event: ICommit<IPurchaseOrderLine>) => {
    if (event.original) {
      selectedRow.current = setIn(
        event.original,
        event.cellKey,
        event.updated![event.cellKey as keyof typeof event.updated]
      );
    } else {
      selectedRow.current = event.updated;
    }
  };

  const archivedLineFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'archived',
        value: 'true',
      },
    },
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'purchaseOrder.id',
        value: id,
      },
    },
  ];

  const { data: archivedLineData } = useQuery<
    IPurchaseOrderLinesWorkspaceQueryResult,
    IPurchaseOrderLinesWorkspaceQueryVariables
  >(PurchaseOrderLine.workspace, {
    variables: { filters: archivedLineFilters },
    skip: id === null || id === undefined,
    fetchPolicy: 'network-only',
  });
  const archived = archivedLineData?.listData?.edges || [];

  //</editor-fold>

  const renderLineActionButtons = ({ rowData, rowIdx }: { rowData: IPurchaseOrderLine; rowIdx: number }) => {
    let renderedButtons: Array<ReactElement> = [];

    if (!rowData.comment) {
      renderedButtons.push(
        <ContextMenuItem
          key={'actionButtonComment'}
          onClick={onAddLineCommentClick}
          label={'Add Comment'}
          rowIdx={rowIdx}
        />
      );
    }

    // It should only be possible to delete rows which have not been received
    if (rowData.received === 0) {
      renderedButtons.push(
        <ContextMenuItem key={'actionButtonDelete'} onClick={onDeleteClick} label={'Delete'} rowIdx={rowIdx} />
      );
    }

    renderedButtons = [
      ...renderedButtons,
      <ContextMenuItem key={'actionButtonAudit'} onClick={onLineAuditClick} label={'View Audit'} rowIdx={rowIdx} />,
      <ContextMenuItem
        key={'actionButtonProduct'}
        onClick={onLineProductClick}
        label={'View Product'}
        rowIdx={rowIdx}
      />,
    ];

    return renderedButtons;
  };

  let isArchived: boolean = true;
  let isInvoiced: boolean = false;
  let isFullyInvoiced: boolean = false;
  if (purchaseOrder) {
    isArchived = !!purchaseOrder.archived;

    if (purchaseOrder.status) {
      isInvoiced = purchaseOrder.status.includes(PurchaseOrderLineStatus.Invoiced);
    }

    if (purchaseOrder.orderLines) {
      isFullyInvoiced = purchaseOrder.orderLines.every(isLineFullyInvoiced);
    }
  }

  const orderNumber = purchaseOrder ? purchaseOrder.orderNumber : '';
  const attachmentsCount: number | undefined = getEntryCount(purchaseOrder?.attachments, true);
  const linesCount: number | undefined = getEntryCount(purchaseOrder?.orderLines);

  const directDelivery: Maybe<boolean> = purchaseOrder?.directDelivery;
  const directDeliveryCustomer: Maybe<ICustomer> = purchaseOrder?.directDeliveryCustomer;
  let directDeliveryCustomerInfo: ReactElement | null = null;

  const contactPredefinedFilter: Array<IFilterInput> = [];

  if (directDeliveryCustomer) {
    if (directDeliveryCustomer.headOffice !== null && directDeliveryCustomer.headOffice !== undefined) {
      // Allow contact selections from the Head office, or any delivery accounts
      contactPredefinedFilter.push({
        filterParameters: {
          filters: [
            {
              filterOperation: FilterOperation.Equals,
              filterParameters: {
                field: 'customer.id',
                value: directDeliveryCustomer.headOffice.id,
              },
            },
            {
              filterOperation: FilterOperation.Equals,
              filterParameters: {
                field: 'customer.headOffice.id',
                value: directDeliveryCustomer.headOffice.id,
              },
            },
          ],
          booleanOperator: BooleanOperator.Or,
        },
      });
    } else {
      contactPredefinedFilter.push({
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'customer.id',
          value: directDeliveryCustomer.id,
        },
      });
    }

    contactPredefinedFilter.push({
      filterOperation: FilterOperation.NotEquals,
      filterParameters: {
        field: 'deliveryType',
        value: 'NOT_ALLOWED',
      },
    });

    directDeliveryCustomerInfo = (
      <InputBlock>
        <InputLabel label="Customer" />
        <InputGroup>
          <a href={`/Customers/Customer/Details/${directDeliveryCustomer.id}`} target="_null">
            {directDeliveryCustomer.reference} - {directDeliveryCustomer.name}
          </a>
        </InputGroup>
      </InputBlock>
    );
  }

  let matchCount = 0;
  let matchLines: any = [];
  const lines: Maybe<Array<Maybe<IPurchaseOrderLine>>> = purchaseOrder.orderLines;
  if (lines) {
    matchLines = lines
      .flatMap((orderLine: Maybe<IPurchaseOrderLine>) => {
        if (!orderLine?.matchedLines) return [];
        return orderLine.matchedLines.map((matchedLine: Maybe<Omit<IPurchaseInvoiceOrderLineMatch, 'relayId'>>) => {
          if (matchedLine) {
            return {
              ...matchedLine,
              purchaseOrderLine: {
                id: orderLine.id,
                uuid: orderLine.uuid,
                concurrencyVersion: orderLine.concurrencyVersion,
                product: orderLine.product,
                description: orderLine.description,
              } as Maybe<IPurchaseOrderLine>,
            };
          }
          return matchedLine;
        });
      })
      .filter((matchLine) => matchLine?.posted);
    matchCount = matchLines?.length;
  }

  const supplierCountry = purchaseOrder.supplier?.address?.country;
  const deliveryCountry = purchaseOrder?.deliveryAddress?.country;

  const carriageCosts = purchaseOrder?.supplier?.carriageCosts;
  // NOTE: This is not the same as the value in carriageCosts, this will
  //       override the one in carriage costs for this order.
  const carriageCostCalculationMethod = purchaseOrder?.carriageCostCalculationMethod;

  const supplierConfig = useSupplierConfig();

  let disallowOrderLineItemModification: boolean = false;
  if (purchaseOrder.status !== null && purchaseOrder.orderNumber !== null) {
    const noStatuses = purchaseOrder.status?.length === 0; //Allow lines to be added to an empty PO.

    disallowOrderLineItemModification = !(
      (!purchaseOrder.matchPosted &&
        supplierConfig?.purchaseOrderMatchingType === PurchaseOrderMatchingType.PurchaseOrder &&
        purchaseOrder.status?.includes(PurchaseOrderLineStatus.Received)) ||
      purchaseOrder.status?.includes(PurchaseOrderLineStatus.AwaitingDelivery) ||
      purchaseOrder.status?.includes(PurchaseOrderLineStatus.ConsideredComplete) ||
      noStatuses
    );
  }

  const purchaseOrderLineWorkspaceColumns: Array<string> = [
    'product.reference',
    'product.description',
    'quantity',
    'received',
    'unitAdditionalCost',
    'totalAdditionalCost',
  ];
  if (allCostViewPermission) {
    purchaseOrderLineWorkspaceColumns.push('grossSellingPrice');
  }

  const goodsReceiptLineWorkspaceColumns: Array<string> = [
    'goodsReceipt.receiptDate',
    'product.reference',
    'product.description',
    'binLocation.location',
    'vendorRating.description',
    'quantity',
    'goodsReceipt.exchangeRate',
  ];
  if (allCostViewPermission) {
    goodsReceiptLineWorkspaceColumns.push('costPrice');
  }

  const matchedLinesColumns: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Purchase Invoice No',
      key: 'purchaseInvoiceLine.purchaseInvoice.documentNumber',
      width: 100,
      formatter: ModalFormatter,
      options: {
        modalContent: LazyPurchaseInvoiceForm,
        modalProps: { forceReadOnly: true },
        entityId: 'purchaseInvoiceLine.purchaseInvoice.id',
      },
    },
    {
      name: 'Order Date',
      key: 'purchaseInvoiceLine.purchaseInvoice.createdAt',
      formatter: Formatters.DateFormatter,
      width: 120,
    },
    {
      name: 'Nominal',
      key: 'purchaseInvoiceLine.nominal.accountNo',
    },
    {
      name: 'Product',
      key: 'purchaseOrderLine.product.reference',
    },
    {
      name: 'Description',
      key: 'purchaseOrderLine.description',
    },

    /**
     * Should only see this screen if for matching and the invoice is posted - If we
     * wanted to see it before posting, should rename these fields appropriately.
     */
    {
      name: 'Invoiced Quantity',
      key: 'invoiceQuantity',
      formatter: 'numeric',
      options: {
        format: {
          decimals: (rowData) => getProductDP(rowData, 'purchaseOrderLine.product'),
        },
      },
    },
  ];
  if (allCostViewPermission) {
    matchedLinesColumns.push({
      name: 'Invoiced Value',
      key: 'invoiceValue',
    });
  }

  return (
    <>
      <ConnectedFormPristinePrompt />
      <Form.FormContainer>
        <CurrencyContext.Provider value={{ currency: purchaseOrder?.currency }}>
          <Form.Header
            entityHdr="orderReference"
            orderNumber={orderNumber}
            component={PurchaseOrderHeaderComponent}
            field="Order"
            statusFlags={PURCHASE_ORDER_STATUS_FLAGS}
            values={purchaseOrder}
            entity={entities.PurchaseOrder}
            noDeleteOption={isArchived}
            saveViaWrappingComponent={saveViaWrappingComponent}
            saveOptions={saveOptions}
            customBreadcrumb={props.customBreadcrumb}
          />
          <Form.Form>
            <Form.Row>
              <Form.Fieldset cols={3 / 12}>
                <Form.Field<IDateFieldProps>
                  dataid="orderDate"
                  component={input.DateField}
                  label="Order Date"
                  labelCols={4 / 12}
                  inputCols={8 / 12}
                />
                <Form.Field<IDateFieldProps>
                  dataid="targetDate"
                  component={input.DateField}
                  label="Expected Delivery Date"
                  labelCols={4 / 12}
                  inputCols={8 / 12}
                  onChange={(event, newValue, oldValue) => {
                    purchaseOrder.orderLines?.forEach((orderLine, index) => {
                      // If the line target date is not set, or matches the current header target date, updated it to the new header target date.
                      if (
                        orderConfig?.automaticallySetLineDeliveryDates &&
                        (!orderLine.targetDate || orderLine.targetDate === oldValue)
                      ) {
                        dispatch(change(PurchaseOrder.type, `orderLines.${index}.targetDate`, newValue));
                      }
                    });
                  }}
                />
                <input.Switch
                  label="Hide invoiced items"
                  onChange={(value) => {
                    setCookie(HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE, value, {
                      expires: add(new Date(), { years: 20 }),
                    });
                  }}
                  labelCols={6 / 12}
                  inputCols={6 / 12}
                  value={cookies[HIDE_PURCHASE_ORDER_INVOICED_LINE_ITEMS_COOKIE] === 'true'}
                  hint={'Hide fully invoiced lines from the Items table. This will not affect the total values.'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={5 / 12}>
                <Form.Field<IDropdownProps>
                  dataid="supplier"
                  component={input.Dropdown}
                  label="Supplier"
                  autoFocus={true}
                  temporaryOnChange={true}
                  readOnly={isInvoiced}
                />
                <AddressInfo address={purchaseOrder.supplier?.address} entityType={'supplier'} />
                <Form.Field
                  dataid="supplier.minimumOrderValue"
                  component={input.BlurTextField}
                  type={BlurTextFieldType.Currency}
                  label="Minimum Order Value"
                  readOnly={true}
                />
                <Form.Field dataid="orderReference" component={input.BlurTextField} label="Order Reference" />
              </Form.Fieldset>

              <Form.Fieldset cols={2 / 12}>
                <OrderTotals
                  currencyCode={currencyCode}
                  carriageCosts={carriageCosts}
                  archived={isArchived}
                  carriageCalculationMethod={purchaseOrder.carriageCostCalculationMethod}
                  originModule={OrderTotalsOriginModule.Purchasing}
                  carriageReadOnly={isFullyInvoiced}
                />
              </Form.Fieldset>
            </Form.Row>
            <Form.TabPanel>
              <Form.TabContent id="main" label="Items" badge={linesCount}>
                <GridContainer>
                  <CommentRowContext.Provider
                    value={{
                      selectRow: setAddCommentToRowIdx,
                      addCommentToRowIdx: addCommentToRowIdx,
                      dataIdPrefix: `orderLines`,
                    }}
                  >
                    <Form.Field<IEditableDataGridProps>
                      temporaryOnChange={true}
                      addBlankRow={true}
                      dataid="orderLines"
                      enableCellSelect={!isArchived}
                      columns={columnMeta}
                      component={AbstractLineGrid}
                      dataStoreId={dataStoreId}
                      contentHeight={itemTableHeight}
                      onCellSelected={onCellSelected}
                      onChange={onLineChange}
                      withRowUpdated={purchaseOrderEntryGridWithRowUpdated}
                      normalize={normalizeOrderLines}
                      temporaryOnFocus={false}
                      ignoreMutationIdCheck={true}
                      rowRenderer={PurchaseOrderRowRenderer}
                      contextKey={'purchaseOrderLines'}
                      enableContextMenu={true}
                      renderActionButtons={renderLineActionButtons}
                      disableVirtualRows={true}
                      readOnly={disallowOrderLineItemModification}
                      autoWidth={true}
                      ignoreKeypresses={productModalState.modalOpen} // Make grid un-tabbable while a Modal is open.
                      selectCellFormatter={PurchaseOrderStatusCellFormatter}
                    />
                  </CommentRowContext.Provider>
                </GridContainer>
                <LineDetailsPanel
                  orderLines={purchaseOrder.orderLines as Array<IPurchaseOrderLine>}
                  rowIndex={rowIdx}
                  field={'orderLines'}
                  currencyCode={currencyCode}
                  type={'PURCHASE'}
                  itemInformationQuery={PurchaseOrderLineItemInformationQuery}
                />
              </Form.TabContent>
              <Form.TabContent id="header" label="Header">
                <Form.Row>
                  <Form.Fieldset cols={2 / 3}>
                    <Form.Legend label="General" />
                    <Form.Field
                      dataid="vatBusinessCategory"
                      component={input.Dropdown}
                      label="Business VAT Status"
                      reformatLabel={false}
                    />
                    <Form.Field dataid="warehouse" component={input.Dropdown} label="Warehouse" temporary={true} />
                    {documentConfig?.purchaseOrderDocumentDefault?.documentsInUse ? (
                      <Form.Field
                        dataid="purchaseOrderDocumentRequired"
                        component={input.Checkbox}
                        label="Purchase Order Document Required"
                      />
                    ) : null}
                    <Form.Field dataid="comment" component={input.BlurTextField} type="textarea" label="Comment" />
                    <Form.Legend label="Pricing" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="overallDiscountPercent"
                      label="Overall Discount"
                      component={input.DiscountInput}
                      prefix="-"
                      suffix="%"
                      temporary={true}
                      readOnly={isFullyInvoiced}
                    />
                    <HeadOfficeInfo purchaseOrder={purchaseOrder} />
                  </Form.Fieldset>
                  {allCostViewPermission ? (
                    <>
                      <Form.Fieldset cols={1 / 3}>
                        <ValueBreakdown />
                      </Form.Fieldset>
                    </>
                  ) : null}
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="attributes" label="Attributes">
                <Form.Row>
                  <Form.Fieldset cols={2 / 3}>
                    <Form.Legend label="Attributes" />
                    <Form.Field<IAttributeArrayProps>
                      dataid="attributeValues"
                      component={input.AttributeArray}
                      label="Attributes"
                      query={entities.PurchaseAttributeConfig.list}
                      initialized={true}
                      type={'purchase'}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="costs" label="Costs" onlyRenderWhenActive={true}>
                <Form.Row>
                  <Form.Fieldset cols={1 / 2}>
                    <Form.Legend label="Purchase Costs" />
                    <QueryDropdown
                      {...entities.PurchaseCostTemplate.dropdown}
                      label="Purchase Cost Template"
                      readOnly={isArchived}
                      onChange={(value: IPurchaseCostTemplate) => {
                        // copy template and delete id and concurrency version
                        const cost = Object.assign({}, value) as IPurchaseCost;
                        delete cost.id;
                        delete cost.concurrencyVersion;
                        delete cost.uuid;
                        cost.uuid = uuIdGenerator(); //UUID must exist - Generate a new one.

                        if (!cost.defaultSupplier) {
                          cost.defaultSupplier = purchaseOrder.supplier;
                        }

                        dispatch(
                          change(PurchaseOrder.type, 'purchaseCosts', (purchaseOrder?.purchaseCosts || []).concat(cost))
                        );
                      }}
                      data-testid={'po-purchase-cost-template'}
                    />
                  </Form.Fieldset>
                  <Form.Field<IEditableDataGridProps>
                    dataid="purchaseCosts"
                    component={input.DataGrid}
                    enableCellSelect={true}
                    addBlankRow={true}
                    columns={PURCHASE_COST_COLUMN_META}
                    autoContentHeight={true}
                    enableContextMenu={true}
                    deleteOption={(rowIdx) => {
                      const dataid = `purchaseCosts.${rowIdx}.deleted`;
                      dispatch(change(PurchaseOrder.type, dataid, true));
                    }}
                  />
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="customs" label="Customs">
                <Form.Legend label="Intrastat" />
                <Form.Field<IDropdownProps>
                  dataid="intrastatDetails.termsOfDelivery"
                  component={input.Dropdown}
                  clearable={true}
                  label="Terms of Delivery"
                  readOnly={isArchived}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid="intrastatDetails.natureOfTransactionCode"
                  label="Nature of Transaction"
                  component={input.BlurTextField}
                  hint={natureOfTransactionHint}
                  type={BlurTextFieldType.FluentMask}
                  mask="99"
                  readOnly={isArchived}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid="intrastatDetails.customsProcedureCode"
                  label="Customs Procedure Code"
                  component={input.BlurTextField}
                  hint={customsProcedureCodeHint}
                  type={BlurTextFieldType.FluentMask}
                  mask="99-99-999"
                  readOnly={isArchived}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid="intrastatDetails.modeOfTransportCode"
                  label="Mode Of Transport Code"
                  component={input.BlurTextField}
                  hint={modeOfTransportHint}
                  type={BlurTextFieldType.FluentMask}
                  mask="9"
                  readOnly={isArchived}
                />
              </Form.TabContent>
              <Form.TabContent
                id="currency"
                label="Currency"
                shouldRender={purchaseOrder?.foreignCurrency ?? false}
                onlyRenderWhenActive={true}
              >
                <Form.Row>
                  <Form.Fieldset cols={1 / 3}>
                    <Form.Row>
                      <Form.Fieldset cols={1} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="exchangeRate"
                          datatype="numeric"
                          component={input.BlurTextField}
                          label={'Exchange Rate'}
                          readOnly={true}
                        />
                      </Form.Fieldset>
                    </Form.Row>

                    <Form.Legend label="Value Breakdown" />
                    <Form.Row>
                      <Form.Fieldset cols={1 / 3} collapsed={true} />
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <ColumnLabel label={currencyCode} textAlign={'right'} />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <ColumnLabel label={company?.baseCurrency?.isoCode?.codeAlpha3} textAlign={'right'} />
                      </Form.Fieldset>
                    </Form.Row>

                    <Form.Row>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Label>Gross Goods</Label>
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="grossGoodsTotal"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.Currency}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="baseGrossGoodsTotal"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.BaseCurrency}
                        />
                      </Form.Fieldset>
                    </Form.Row>

                    <Form.Row>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Label>Discount</Label>
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="overallDiscountValue"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.Currency}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="baseOverallDiscountValue"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.BaseCurrency}
                        />
                      </Form.Fieldset>
                    </Form.Row>

                    <Form.Row>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Label>Net Goods</Label>
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="nettGoodsTotal"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.Currency}
                          decimals={2}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="baseNettGoodsTotal"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.BaseCurrency}
                          decimals={2}
                        />
                      </Form.Fieldset>
                    </Form.Row>
                    <Form.Row>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Label>Order Total</Label>
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="headerTotal"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.Currency}
                          decimals={2}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 3} collapsed={true}>
                        <Form.Field<IBlurTextFieldProps>
                          dataid="baseHeaderTotal"
                          datatype="numeric"
                          component={input.BlurTextField}
                          readOnly={true}
                          includeLabel={false}
                          type={BlurTextFieldType.BaseCurrency}
                          decimals={2}
                        />
                      </Form.Fieldset>
                    </Form.Row>
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="delivery" label="Delivery" onlyRenderWhenActive={true}>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label={directDelivery ? 'Direct Delivery to Customer' : 'Delivery'} />
                    {directDelivery && (
                      <Form.Field<IDropdownProps>
                        dataid="directDeliveryContact"
                        component={input.Dropdown}
                        label="Contact"
                        predefinedFilters={contactPredefinedFilter}
                        temporaryOnChange={true}
                        remoteFilter={true}
                        optionRenderer={ContactOptionRenderer}
                        customIcon={StandaloneGlobalContactLookupWrapper}
                        onChange={(value) => {
                          props.change('deliveryAddress', value.address);
                          props.change('deliveryName', value.name.fullName);
                        }}
                      />
                    )}

                    {directDelivery ? directDeliveryCustomerInfo : null}
                    <Form.Field<IBlurTextFieldProps>
                      dataid="deliveryName"
                      label="Shipment Name"
                      component={input.BlurTextField}
                    />
                    <ConnectedAddress
                      dataid={'deliveryAddress'}
                      labelOverrides={{ line1: 'Shipment Address', line2: '', line3: '', line4: '', line5: '' }}
                      loqateProps={{ companyDataid: 'deliveryName' }}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="deliveryPhoneNumber"
                      label="Phone Number"
                      component={input.BlurTextField}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="deliveryMobileNumber"
                      label="Mobile Number"
                      component={input.BlurTextField}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="deliveryEmail"
                      label="Email"
                      component={input.BlurTextField}
                      hyperlink={HrefLink.MailTo}
                    />
                    <Form.Legend label="Carriage" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="weightTotal"
                      label="Net Product Weight"
                      component={input.BlurTextField}
                      readOnly={true}
                      temporary={true}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="packagingWeightTotal"
                      label="Packaging Weight"
                      component={input.BlurTextField}
                      readOnly={true}
                      temporary={true}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="grossWeightTotal"
                      label="Gross Order Weight"
                      component={input.BlurTextField}
                      readOnly={true}
                      temporary={true}
                    />
                    <Form.Field<ITextFieldProps>
                      dataid="packagingVolumeTotal"
                      label="Packaging Volume"
                      component={FluentTextField}
                      suffix="m³"
                      readOnly={true}
                      temporary={true}
                    />
                    <Form.Field
                      dataid="carriageCostCalculationMethod"
                      label="Carriage Calculation Method"
                      component={input.Dropdown}
                      temporary={true}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="carriageTotal"
                      label="Carriage Total"
                      component={input.BlurTextField}
                      readOnly={carriageCostCalculationMethod === 'CALCULATED'}
                      temporary={true}
                      type={BlurTextFieldType.Number}
                      decimals={2}
                    />
                    <Form.Field
                      dataid="carriageVatProductCategory"
                      label="Carriage VAT Category"
                      component={input.Dropdown}
                      temporary={true}
                      reformatLabel={false}
                    />
                  </Form.Fieldset>
                </Form.Row>
                {supplierCountry?.id !== deliveryCountry?.id ? (
                  <Form.Row>
                    <Form.Fieldset cols={1}>
                      <Form.Legend label="Customs" />
                      <Form.Field dataid="termsOfDelivery" component={input.Dropdown} label="Terms of Delivery" />
                      <Form.Field<IBlurTextFieldProps>
                        dataid="natureOfTransactionCode"
                        label="Nature of Transaction"
                        component={input.BlurTextField}
                        temporary={true}
                        hint={"Code must be of the format '99'"}
                        type={BlurTextFieldType.FluentMask}
                        mask="99"
                      />
                      <Form.Field<IBlurTextFieldProps>
                        dataid="customsProcedureCode"
                        label="Customs Procedure Code"
                        component={input.BlurTextField}
                        temporary={true}
                        hint={"Code must be of the format '99-99-999'"}
                        type={BlurTextFieldType.FluentMask}
                        mask="99-99-999"
                      />
                    </Form.Fieldset>
                  </Form.Row>
                ) : null}
              </Form.TabContent>
              <Form.TabContent
                id="events"
                label="Diary"
                shouldRender={getIdFromProps(props) !== 'New'}
                onlyRenderWhenActive={true}
              >
                <GridContainer>
                  <DiaryEventWorkspace
                    embeddedView={true}
                    predefinedFilters={eventPredefinedFilters}
                    viewScreenName="/Diary/Task/Modal"
                    removeAddButton={false}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="documents"
                label="Documents"
                shouldRender={getIn(props, 'match.params.id') !== 'New'}
                onlyRenderWhenActive={true}
              >
                <DocumentSummaryWorkspace
                  preview={true}
                  embeddedView={true}
                  predefinedFilters={[
                    {
                      filterOperation: FilterOperation.Equals,
                      filterParameters: {
                        field: 'transactionId',
                        value: purchaseOrder.id,
                      },
                    },
                    {
                      filterOperation: FilterOperation.Equals,
                      filterParameters: {
                        field: 'transactionEntity',
                        value: 'PURCHASE_ORDER',
                      },
                    },
                  ]}
                  viewScreenName="/Common/DocumentSummary/Modal"
                  removeAddButton={true}
                />
              </Form.TabContent>
              <Form.TabContent
                id="attachments"
                label="Attachments"
                badge={attachmentsCount}
                onlyRenderWhenActive={true}
              >
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Attachments" />
                    <Form.Field<IGridUploaderProps>
                      dataid="attachments"
                      component={input.GridUploader}
                      forceReadOnly={false}
                      additionalColumns={[
                        {
                          name: 'Attach on Documents',
                          key: 'includeOnDocuments',
                          widthFactor: 1,
                          formatter: Formatters.CheckFormatter,
                          editor: <TypedCheckboxEditor />,
                          editable: true,
                          resizable: true,
                          draggable: false,
                        },
                      ]}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="notesTab"
                label="Notes"
                badge={purchaseMemoEditorProps.queryProps.data?.listData?.count}
                type="info"
                onlyRenderWhenActive={true}
                shouldRender={getIdFromProps(props) !== 'New'}
              >
                <MemoEditor noteTypeEntity={PurchaseNoteType} {...purchaseMemoEditorProps} />
              </Form.TabContent>
              <Form.TabContent
                id="supplierNotesTab"
                label="Supplier Notes"
                badge={supplierMemoEditorProps.queryProps.data?.listData?.count}
                type="info"
                onlyRenderWhenActive={true}
                shouldRender={getIn(props, 'values.supplier.id') > 0}
              >
                <MemoEditor noteTypeEntity={SupplierNoteType} {...supplierMemoEditorProps} path={'supplier'} />
              </Form.TabContent>
              <Form.TabContent id="lineDetails" label="Line details" onlyRenderWhenActive={true}>
                <LazyPurchaseOrderLineWorkspace
                  embeddedView={true}
                  allowView={false}
                  columnData={purchaseOrderLineWorkspaceColumns}
                  initialSortColumn="id"
                  predefinedFilters={[
                    {
                      filterOperation: 'EQUALS',
                      filterParameters: {
                        field: 'purchaseOrder.id',
                        value: getIdFromPropsForFilter(props),
                      },
                    },
                    {
                      filterOperation: 'EQUALS',
                      filterParameters: {
                        field: 'purchaseOrder.concurrencyVersion',
                        value: purchaseOrder?.concurrencyVersion,
                      },
                    },
                  ]}
                  noEdit={true}
                  viewScreenName={'/Purchasing/OrderLines/Modal/'}
                  contentHeight={itemTableHeight}
                  onRowClick={(row: IPurchaseOrderLine) => {
                    setLineDetailsSelectedRowUuid(row.uuid);
                  }}
                />
                {lineDetailsSelectedRowIndex !== undefined && lineDetailsSelectedRowIndex >= 0 ? (
                  <>
                    <Form.Field<IEditableDataGridProps>
                      temporaryOnChange={false}
                      addBlankRow={false}
                      dataid={`orderLines.${lineDetailsSelectedRowIndex}.assignments`}
                      className="entryGrid"
                      columns={ASSIGNED_COLUMNS}
                      component={input.DataGrid}
                      dataStoreId={dataStoreId}
                      contentHeight={itemTableHeight}
                    />
                    <Form.Field<IEditableDataGridProps>
                      temporaryOnChange={false}
                      addBlankRow={false}
                      dataid={`orderLines.${lineDetailsSelectedRowIndex}.assemblyLineAssignments`}
                      className="entryGrid"
                      columns={ASSEMBLY_ASSIGNED_COLUMNS}
                      component={input.DataGrid}
                      dataStoreId={dataStoreId}
                      contentHeight={itemTableHeight}
                    />
                  </>
                ) : null}
              </Form.TabContent>
              <Form.TabContent id="receipts" label="Receipts">
                <GridContainer>
                  <GoodsReceiptLineWorkspace
                    embeddedView={true}
                    predefinedFilters={[
                      {
                        filterOperation: 'EQUALS',
                        filterParameters: {
                          field: 'purchaseOrderLine.purchaseOrder.id',
                          value: getIdFromPropsForFilter(props),
                        },
                      },
                    ]}
                    viewScreenName="/Purchasing/GoodsReceiptLine/Workspace"
                    defaultColumns={goodsReceiptLineWorkspaceColumns}
                    initialSortColumn="goodsReceipt.receiptDate"
                    initialSortDir={Direction.Desc}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="archived"
                label="Archived"
                key="archived"
                shouldRender={getIdFromProps(props) !== 'New'}
                onlyRenderWhenActive={true}
              >
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <EditableDataGrid
                      dataid="archived"
                      initialAddingNewNote={false}
                      columns={archivedColumnMeta}
                      listData={archived}
                      rowsCount={archived.length}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="audit"
                label="Audit"
                key="audit"
                shouldRender={getIdFromProps(props) !== 'New'}
                onlyRenderWhenActive={true}
              >
                <GridContainer>
                  <AuditByFieldWorkspace
                    embeddedView={true}
                    predefinedFilters={purchaseOrderAuditPredefinedFilters}
                    viewScreenName="/sales/PurchaseOrderAudit/Workspace"
                    entity={PurchaseOrderAuditByField}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="matchedLines"
                label="Matched Lines"
                badge={matchCount}
                shouldRender={matchLines && matchLines.length > 0}
                onlyRenderWhenActive={true}
              >
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Matched Lines" />
                    <EditableDataGrid
                      enableCellSelect={false}
                      listData={matchLines}
                      columns={matchedLinesColumns}
                      contentHeight={itemTableHeight}
                      rowsCount={matchCount}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
            </Form.TabPanel>
          </Form.Form>
        </CurrencyContext.Provider>
      </Form.FormContainer>
    </>
  );
};
const ConnectedPurchaseOrderForm = createForm({
  form: type,
  name: formTitle,
  entity: entities.PurchaseOrder,
  queryFragment: fragment,
  defaultReturnUrl: workspaceUrl,
  getSubmitValues: submitValues,
  forceUpdate: true,
  modal: {
    PRINT_PURCHASE_ORDER_DOCUMENT: {
      render: (props) => <PurchaseOrderDocumentRenderer {...props} />,
    },
  },
})(PurchaseOrderForm);

const OrderForm = forwardRef(({ overrideParams, detailView, ...props }: any, ref) => {
  const formQueryParams = overrideParams || props?.match?.params;

  const supplierId = props.match?.params?.supplierid;
  const defaultVariables = { supplierId: supplierId };

  const formDefault = useFormQuery<IPurchaseOrder>({ default: defaultQuery, instance: instance } as EntityType, {
    duplicate: true,
    overrideParams: formQueryParams,
    defaultVariables,
    skip: !!props.formData?.defaultRecord,
  });
  const cacheUpdate = useEntityCacheUpdate('purchaseOrders', 'purchaseOrder', { detailView });
  const [formUpdate] = useMutation(update!, {
    update: cacheUpdate,
    refetchQueries: [PurchaseNotesWorkspaceQuery, SupplierNotesWorkspaceQuery],
  });
  const [formTemporaryUpdate] = useMutation(temporarySave!);
  const dispatch = useDispatch();
  const supplierConfig = useSupplierConfig();
  const orderConfig = usePurchaseOrderConfig();

  return (
    <ConnectedPurchaseOrderForm
      ref={ref}
      formData={formDefault}
      {...props}
      formUpdate={formUpdate}
      formTemporaryUpdate={formTemporaryUpdate}
      dispatch={dispatch}
      orderConfig={orderConfig}
      supplierConfig={supplierConfig}
    />
  );
});

export default OrderForm;
